import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api } from '@/lib/api'

/**
 * Providers tab content for the Supply Chain surface.
 *
 * Lists every tracked supply-chain / identity provider with rolling
 * 30d / 90d incident counts and last-critical timestamp. Wraps
 * `GET /api/providers`.
 *
 * Extracted from the legacy /providers page (PR #582) so the same
 * surface can render inside the Supply Chain tab strip alongside the
 * incidents feed. Provider detail links continue to point at
 * `/providers/:slug` (deep-linkable, unchanged).
 *
 * Owns its own outer description text since the page-level header is
 * shared with the Incidents tab.
 */

interface ProviderSummary {
  id: string
  slug: string
  name: string
  category: 'cloud' | 'identity'
  homepageUrl: string | null
  statusPageUrl: string | null
  bulletinUrl: string | null
  description: string | null
  active: boolean
  stats: {
    last30d: number
    last90d: number
    lastCriticalAt: string | null
  }
}

interface ProvidersResponse {
  providers: ProviderSummary[]
}

export function ProvidersView() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['providers', 'list'],
    queryFn: () => api.get<ProvidersResponse>('/providers'),
    staleTime: 60_000,
  })

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Cloud and identity infrastructure providers tracked for security
        incidents, status-page outages, and CVE advisories. Rolling 30 / 90
        day counts exclude suppressed rows.
      </p>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading providers…</p>
      ) : isError || !data ? (
        <p className="text-sm text-muted-foreground">
          Provider list unavailable.
        </p>
      ) : data.providers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No providers configured.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.providers.map((p) => (
            <ProviderCard key={p.id} provider={p} />
          ))}
        </div>
      )}
    </div>
  )
}

function ProviderCard({ provider }: { provider: ProviderSummary }) {
  return (
    <Card
      className={
        'overflow-hidden transition-colors hover:border-primary/40 ' +
        (provider.active ? '' : 'opacity-60')
      }
    >
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/providers/${provider.slug}`}
            className="text-base font-semibold hover:underline"
          >
            {provider.name}
          </Link>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-xs capitalize">
              {provider.category}
            </Badge>
            {!provider.active && (
              <Badge variant="outline" className="text-xs">
                deprecated
              </Badge>
            )}
          </div>
        </div>

        {provider.description && (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {provider.description}
          </p>
        )}

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              30d
            </dt>
            <dd className="font-medium tabular-nums">
              {provider.stats.last30d}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              90d
            </dt>
            <dd className="font-medium tabular-nums">
              {provider.stats.last90d}
            </dd>
          </div>
        </dl>

        {provider.stats.lastCriticalAt && (
          <p className="text-xs text-muted-foreground">
            Last critical:{' '}
            <span className="tabular-nums">
              {format(new Date(provider.stats.lastCriticalAt), 'yyyy-MM-dd')}
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export default ProvidersView
